import { useParams } from "react-router-dom";

import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useToast } from "@/hooks/use-toast";
import { useState } from "react";
import { useLocation } from "react-router-dom";
import MeetingRequest from "./MeetingRequest";

const formConfigs = {
  "baillie-gifford": {
    title: "Baillie Gifford European Growth Fund - Meeting Request",
    description: "Please complete this form to request a meeting with the Baillie Gifford European Growth Fund team.",
    fields: [
      { name: "companyName", label: "Company Name", type: "text", required: true },
      { name: "contactName", label: "Contact Name", type: "text", required: true },
      { name: "email", label: "Email Address", type: "email", required: true },
      { name: "phone", label: "Phone Number", type: "tel", required: false },
      { name: "meetingType", label: "Meeting Type", type: "select", required: true, options: [
        "Quarterly Review",
        "Strategy Discussion",
        "Performance Analysis",
        "Market Trends",
        "Other"
      ]},
      { name: "preferredDate", label: "Preferred Meeting Date", type: "date", required: true },
      { name: "topics", label: "Topics to Discuss", type: "textarea", required: false }
    ]
  },
  "trowe-price": {
    title: "T. Rowe Price Global Technology Fund - Meeting Request",
    description: "Please complete this form to request a meeting with the T. Rowe Price Global Technology Fund team.",
    fields: [
      { name: "companyName", label: "Company Name", type: "text", required: true },
      { name: "contactName", label: "Contact Name", type: "text", required: true },
      { name: "email", label: "Email Address", type: "email", required: true },
      { name: "phone", label: "Phone Number", type: "tel", required: false },
      { name: "investmentFocus", label: "Investment Focus", type: "select", required: true, options: [
        "Artificial Intelligence",
        "Cloud Computing",
        "Cybersecurity",
        "Fintech",
        "Healthcare Technology",
        "Other Technology Sectors"
      ]},
      { name: "preferredDate", label: "Preferred Meeting Date", type: "date", required: true },
      { name: "specificQuestions", label: "Specific Questions or Topics", type: "textarea", required: false }
    ]
  }
};

export default function FundManagerForms() {
  const { formId } = useParams<{ formId: string }>();
  const location = useLocation();
  const { toast } = useToast();
  const [formData, setFormData] = useState<Record<string, string>>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  
  // Check if we're on the meeting request route
  if (location.pathname === "/meeting-request") {
    return <MeetingRequest />;
  }

  const config = formConfigs[formId as keyof typeof formConfigs];

  if (!config) {
    return (
      <>
        <div className="container mx-auto px-4 py-8">
          <Card>
            <CardHeader>
              <CardTitle>Form Not Found</CardTitle>
              <CardDescription>
                The requested form could not be found.
              </CardDescription>
            </CardHeader>
          </Card>
        </div>
      </>
    );
  }

  const handleInputChange = (name: string, value: string) => {
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);

    // Validate required fields
    const missingFields = config.fields
      .filter(field => field.required && !formData[field.name])
      .map(field => field.label);

    if (missingFields.length > 0) {
      toast({
        title: "Missing Required Fields",
        description: `Please complete: ${missingFields.join(', ')}`,
        variant: "destructive",
      });
      setIsSubmitting(false);
      return;
    }

    try {
      // Here you would normally submit to your backend
      // For now, we'll simulate a successful submission
      await new Promise(resolve => setTimeout(resolve, 1000));

      toast({
        title: "Meeting Request Submitted",
        description: "Your meeting request has been submitted successfully. We'll contact you within 2 business days.",
      });

      // Reset form
      setFormData({});
    } catch (error) {
      toast({
        title: "Submission Error",
        description: "There was an error submitting your request. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <>
      <div className="container mx-auto px-4 py-8 max-w-2xl">
        <Card>
          <CardHeader>
            <CardTitle>{config.title}</CardTitle>
            <CardDescription>{config.description}</CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-6">
              {config.fields.map((field) => (
                <div key={field.name} className="space-y-2">
                  <Label htmlFor={field.name}>
                    {field.label}
                    {field.required && <span className="text-red-500 ml-1">*</span>}
                  </Label>
                  
                  {field.type === "text" || field.type === "email" || field.type === "tel" || field.type === "date" ? (
                    <Input
                      id={field.name}
                      type={field.type}
                      value={formData[field.name] || ""}
                      onChange={(e) => handleInputChange(field.name, e.target.value)}
                      required={field.required}
                    />
                  ) : field.type === "select" ? (
                    <Select
                      value={formData[field.name] || ""}
                      onValueChange={(value) => handleInputChange(field.name, value)}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Please select..." />
                      </SelectTrigger>
                      <SelectContent>
                        {field.options?.map((option) => (
                          <SelectItem key={option} value={option}>
                            {option}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  ) : field.type === "textarea" ? (
                    <Textarea
                      id={field.name}
                      value={formData[field.name] || ""}
                      onChange={(e) => handleInputChange(field.name, e.target.value)}
                      rows={4}
                      required={field.required}
                    />
                  ) : null}
                </div>
              ))}
              
              <Button type="submit" disabled={isSubmitting} className="w-full">
                {isSubmitting ? "Submitting..." : "Submit Meeting Request"}
              </Button>
            </form>
          </CardContent>
        </Card>
      </div>
    </>
  );
}